import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  KNOWN_LOCALE_CODES,
  FALLBACK_LOCALE_CODE,
  getEnabledLocaleCodes,
} from '@/lib/i18n/localeRegistry';

// ---------------------------------------------------------------------------
// Dynamic translation loading (P3 F9 multi-language architecture).
//
// Every `src/locales/<locale>/<namespace>.json` file is discovered at build
// time by Vite's `import.meta.glob({ eager: true })`. Adding a new locale is
// therefore as simple as dropping a `src/locales/<code>/` folder + registering
// the code in `lib/i18n/localeRegistry.ts` — no edits to this file are needed.
// ---------------------------------------------------------------------------
const localeModules = import.meta.glob('./locales/*/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>;

// Module locales: `src/modules/<id>/locales/<locale>.json` are glob-merged under
// the namespace `module_<id_snake>` (mirrors the backend module_<id> namespace).
// A module that is not in the repo contributes nothing — same discovery model
// as the core i18n glob above. See docs/architecture/MODULE_SYSTEM_SPEC.md §3.
const moduleLocaleModules = import.meta.glob('./modules/*/locales/*.json', {
  eager: true,
}) as Record<string, { default: Record<string, unknown> }>;

/** locale → namespace → translation object, built from the glob result. */
const resources: Record<string, Record<string, Record<string, unknown>>> = {};
/** Set of namespaces seen across all locales (drives i18next `ns`). */
const namespaceSet = new Set<string>();
/** Set of locale folders actually present on disk. */
const discoveredLocaleSet = new Set<string>();

for (const path in localeModules) {
  // Path shape: ./locales/<locale>/<namespace>.json
  const match = path.match(/\.\/locales\/([^/]+)\/(.+)\.json$/);
  if (!match) continue;
  const [, locale, namespace] = match;
  discoveredLocaleSet.add(locale);
  namespaceSet.add(namespace);
  resources[locale] ??= {};
  resources[locale][namespace] = localeModules[path].default;
}

// Merge module locales under `module_<id_snake>` namespaces.
for (const path in moduleLocaleModules) {
  // Path shape: ./modules/<id>/locales/<locale>.json
  const match = path.match(/\.\/modules\/([^/]+)\/locales\/([^/]+)\.json$/);
  if (!match) continue;
  const [, moduleId, locale] = match;
  const namespace = `module_${moduleId.replace(/-/g, '_')}`;
  discoveredLocaleSet.add(locale);
  namespaceSet.add(namespace);
  resources[locale] ??= {};
  resources[locale][namespace] = moduleLocaleModules[path].default;
}

const namespaces = Array.from(namespaceSet).sort();

// Locales we will actually accept = enabled (from registry) ∩ assets on disk.
// Before backend hydration the registry returns all known locales, so en/es
// behaviour is preserved exactly. Unknown/disabled folders are never offered to
// browser-locale detection (prevents BUG-E31-style foreign month names).
const supportedLngs = getEnabledLocaleCodes().filter((code) => discoveredLocaleSet.has(code));

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: FALLBACK_LOCALE_CODE,
    // Only accept translations we actually ship + have enabled. Prevents
    // browser-locale detection from accepting unsupported languages (e.g.
    // Norwegian "nb") and causing Intl.DateTimeFormat to return
    // foreign-language month names (root cause of BUG-E31).
    supportedLngs: supportedLngs.length > 0 ? supportedLngs : [FALLBACK_LOCALE_CODE],
    nonExplicitSupportedLngs: false, // 'nb-NO' must not fall through as 'nb'
    load: 'languageOnly', // Normalise region variants: 'en-GB' → 'en', 'es-ES' → 'es'
    defaultNS: 'common',
    ns: namespaces,

    // Language detection settings
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Debug mode in development
    // Keep useful development diagnostics while avoiding thousands of
    // test-only initialization messages. Warnings and errors remain enabled.
    debug: import.meta.env.DEV && import.meta.env.MODE !== 'test',

    // React options
    react: {
      useSuspense: true,
    },
  });

// ---------------------------------------------------------------------------
// BUG-E42: Clear stale localStorage values that pre-date the supportedLngs
// restriction (e.g. 'nb' written by an earlier session on a Norwegian-locale
// Chrome). Runs synchronously before the React tree mounts because i18n.ts is
// imported by main.tsx at module-evaluation time. The accept-list is derived
// from the locale registry (all known codes) so a freshly-added language is
// never wiped before the runtime enable-list is hydrated.
// ---------------------------------------------------------------------------
const _storedLng = localStorage.getItem('i18nextLng');
if (_storedLng && !KNOWN_LOCALE_CODES.includes(_storedLng.split('-')[0])) {
  localStorage.removeItem('i18nextLng');
  // Let i18next re-run detection against the now-clean storage; the
  // supportedLngs guard will normalise any remaining navigator.language value
  // to a supported locale (or fall back to the fallback language).
  i18n.changeLanguage(undefined as unknown as string).catch(() => {});
}

export default i18n;
